import React from 'react'
import Icon, { type IconTypes } from '@components/utils/Icon'
import cn from '@components/utils/cn'
import { scopedTranslation } from '@utils/I18n'

type AlertProps = {
  type: 'error' | 'info' | 'warning' | 'success'
  size?: 'sm' | 'md'
  title?: string
  children?: React.ReactNode
  className?: string
  icon?: IconTypes
  onClose?: () => void
  'data-testid'?: string
  role?: 'alert'
}

const containerClassesByType = {
  info: 'tw:border tw:border-primary-300 tw:bg-primary-50/50 tw:text-primary-600',
  warning: 'tw:border tw:border-warning-300 tw:bg-warning-50/50 tw:text-warning-700',
  error: 'tw:border tw:border-error-300 tw:bg-error-50/50 tw:text-error-600',
  success: 'tw:border tw:border-success-300 tw:bg-success-50/50 tw:text-success-700',
} as const

// The icon box is one line tall in its own font size, so a small alert sets the body's
// text size here (and the title's on the icon) to keep the icon centred on the first line.
const sizeClasses = {
  sm: 'tw:p-2 tw:text-sm',
  md: 'tw:p-4',
} as const

const titleClassesByType = {
  info: 'tw:text-primary-700',
  warning: 'tw:text-warning-700',
  error: 'tw:text-error-700',
  success: 'tw:text-success-700',
} as const

const closeIconClassesByType = {
  info: 'tw:text-primary-600 tw:hover:text-primary-800',
  warning: 'tw:text-warning-600 tw:hover:text-warning-800',
  error: 'tw:text-error-600 tw:hover:text-error-800',
  success: 'tw:text-success-600 tw:hover:text-success-800',
} as const

const t = scopedTranslation('shared')

const iconTypes = {
  info: 'info-circle',
  warning: 'alert-triangle',
  error: 'alert-circle',
  success: 'check',
} as const

export default function Alert({ type, title, className, children, onClose, size = 'md', icon, ...props }: AlertProps) {
  const iconName = icon ?? iconTypes[type]

  return (
    <div
      className={cn(
        'tw:flex tw:items-start tw:rounded-lg',
        size === 'sm' ? 'tw:gap-2' : 'tw:gap-3',
        containerClassesByType[type],
        sizeClasses[size],
        className
      )}
      {...props}
    >
      <span className={cn('tw:flex tw:h-[1lh] tw:shrink-0 tw:items-center', title && 'tw:text-base')}>
        <Icon width={size === 'sm' ? 16 : 20} height={size === 'sm' ? 16 : 20} type={iconName} />
      </span>
      <span className="tw:flex tw:min-w-0 tw:flex-col tw:break-words">
        {title && <h3 className={cn('tw:text-base tw:font-medium', titleClassesByType[type])}>{title}</h3>}
        {children}
      </span>
      {typeof onClose === 'function' && (
        <button
          type="button"
          className={cn('tw:cursor-pointer tw:p-2 tw:-m-1 tw:ml-auto', closeIconClassesByType[type])}
          onClick={onClose}
          aria-label={t('buttons.dismiss')}
        >
          <Icon width={12} height={12} type="x-close" />
        </button>
      )}
    </div>
  )
}
